import React, { useCallback, useEffect, useState } from "react";
import { adsBrandCoverage } from "../../services/api";

/**
 * What the numbers on this page can be trusted to say.
 *
 * Every count in the matrix is a count of what we managed to collect, which is
 * not the same as what exists. Two things can make it incomplete rather than a
 * total: a collection run that stopped at the page cap, and creatives whose
 * image has not been read into the structure the report counts over.
 *
 * The panel states both, and for anything unread it says which of the three
 * reasons applies, because "62% covered" with no follow-up gets ignored.
 */

const TONE = {
  ok:    { bg: "#ecfdf5", border: "#a7f3d0", ink: "#065f46", dot: "#059669" },
  warn:  { bg: "#fffbeb", border: "#fde68a", ink: "#92400e", dot: "#d97706" },
  bad:   { bg: "#fef2f2", border: "#fecaca", ink: "#991b1b", dot: "#dc2626" },
  idle:  { bg: "#f9fafb", border: "#e5e7eb", ink: "#374151", dot: "#9ca3af" },
};

function Stat({ label, value, hint, tone = "idle" }) {
  const t = TONE[tone] || TONE.idle;
  return (
    <div style={{ padding: "10px 14px", minWidth: 118 }} title={hint || ""}>
      <div style={{
        fontSize: 20, fontWeight: 900, color: t.ink, lineHeight: 1.1,
        fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginTop: 3 }}>
        {label}
      </div>
    </div>
  );
}

function Banner({ tone, children }) {
  const t = TONE[tone] || TONE.idle;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 8,
      background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: "9px 12px", fontSize: 12.5, fontWeight: 600, color: t.ink, lineHeight: 1.45,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: 999, background: t.dot,
        flexShrink: 0, marginTop: 5,
      }} />
      <div>{children}</div>
    </div>
  );
}

export default function AdsCoveragePanel({ brandId, source, includeCompetitors }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setErr(null);
    try {
      setData(await adsBrandCoverage(brandId, source, includeCompetitors));
    } catch (e) {
      setErr(e?.message || "Could not load coverage");
    } finally {
      setLoading(false);
    }
  }, [brandId, source, includeCompetitors]);

  useEffect(() => { load(); }, [load]);

  if (!brandId) return null;
  if (err) {
    return (
      <div style={{ marginBottom: 10 }}>
        <Banner tone="warn">Coverage unavailable: {err}</Banner>
      </div>
    );
  }
  if (!data && loading) {
    return (
      <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 700, padding: "6px 2px", marginBottom: 10 }}>
        Checking coverage…
      </div>
    );
  }
  if (!data) return null;

  const {
    ads_total, extractable, read_current, read_stale, failing, never_attempted,
    coverage_pct, truncated_runs, last_run_truncated, no_snapshot,
    extraction_enqueued_last_run, failing_sample = [],
    deferred = 0, capture_breaker = null,
  } = data;

  const blockedMins = capture_breaker?.open
    ? Math.max(1, Math.round((capture_breaker.seconds_remaining || 0) / 60))
    : 0;

  const pct = coverage_pct == null ? null : coverage_pct;
  const covTone = pct == null ? "idle" : pct >= 95 ? "ok" : pct >= 60 ? "warn" : "bad";

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff",
      marginBottom: 12, overflow: "hidden",
    }}>
      {/* Counts, and what qualifies them */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0 }}>
        <Stat
          label={last_run_truncated || truncated_runs > 0 ? "in storage, so far" : "in storage"}
          value={last_run_truncated || truncated_runs > 0 ? `${ads_total}+` : ads_total}
          tone={truncated_runs > 0 ? "warn" : "idle"}
          hint={
            last_run_truncated || truncated_runs > 0
              ? "At least this many. A run stopped before the end, so the brand has more ads than we hold."
              : "Every ad ever collected for this brand and still retained. Built up across runs, deduplicated."
          }
        />
        <Stat
          label="images read"
          value={pct == null ? "—" : `${pct}%`}
          tone={covTone}
          hint={`${read_current} of ${extractable} creatives have a current structured read`}
        />
        {never_attempted > 0 && (
          <Stat label="not yet read" value={never_attempted} tone="warn"
                hint="Queued or awaiting a fetch run" />
        )}
        {failing > 0 && (
          <Stat label="failing" value={failing} tone="bad"
                hint="Extraction was attempted and errored" />
        )}
        {deferred > 0 && (
          <Stat label="waiting on a screenshot" value={deferred} tone="warn"
                hint="The provider would not serve the ad page, so there was no image to read. These are not broken and will be picked up once captures resume." />
        )}
        {read_stale > 0 && (
          <Stat label="outdated read" value={read_stale} tone="warn"
                hint="Read under an older schema version; re-extraction refreshes them" />
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8,
            padding: "6px 11px", margin: "0 12px", cursor: "pointer",
            fontSize: 12, fontWeight: 800, color: "#374151", whiteSpace: "nowrap",
          }}
        >
          {open ? "Hide detail" : "What this means"}
        </button>
      </div>

      {/* Warnings that change how the numbers should be read */}
      {(truncated_runs > 0 || pct === 0 || failing > 0 || deferred > 0 || capture_breaker?.open) && (
        <div style={{ display: "grid", gap: 6, padding: "0 12px 12px" }}>
          {truncated_runs > 0 && (
            <Banner tone="warn">
              {truncated_runs === 1 ? "One fetch run" : `${truncated_runs} fetch runs`} stopped
              at the page limit while the provider still had more to give, so this brand has more
              ads than we hold. Narrow the date range or raise the per-run page limit, then
              collect again.
            </Banner>
          )}
          {pct === 0 && extractable > 0 && (
            <Banner tone="bad">
              None of the {extractable} creatives have had their image read yet, so any breakdown
              by what the ads show (people, colours, tone, funnel stage) will be empty. Run a
              fetch to queue them, or run the backfill script.
            </Banner>
          )}
          {failing > 0 && (
            <Banner tone="bad">
              {failing} {failing === 1 ? "creative has" : "creatives have"} failed extraction and
              will stay uncounted until fixed. Most failures are a missing screenshot.
            </Banner>
          )}
          {capture_breaker?.open && (
            <Banner tone="warn">
              Screenshot captures are paused for about {blockedMins} {blockedMins === 1 ? "minute" : "minutes"}.
              The provider refused an earlier request ({capture_breaker.last_reason || "HTTP 403"}), and
              continuing to ask would extend the block. Nothing is wrong with the ads themselves, and
              captures resume automatically. Coverage will not move until then.
            </Banner>
          )}
          {deferred > 0 && !capture_breaker?.open && (
            <Banner tone="warn">
              {deferred} {deferred === 1 ? "creative is" : "creatives are"} waiting on a screenshot
              the provider declined to serve. They are counted as unread rather than failed, and will
              be retried on the next fetch run.
            </Banner>
          )}
        </div>
      )}

      {/* Detail */}
      {open && (
        <div style={{
          borderTop: "1px solid #f3f4f6", padding: "12px 14px",
          background: "#fafafa", display: "grid", gap: 10,
        }}>
          <div style={{
            fontSize: 12.5, color: "#374151", lineHeight: 1.6, maxWidth: 760,
            display: "grid", gap: 6, paddingBottom: 4,
          }}>
            <div><b>Storage</b>: every ad ever fetched for this brand and still
              retained. It grows across runs and never contains the same ad twice.</div>
            <div><b>Run</b>: one fetch, at a point in time, against the criteria you
              set. A run adds ads to storage and skips the ones already there,
              so the ads it <i>added</i> is usually far fewer than the ads it fetched.</div>
            <div><b>Advertiser</b>: the account that published the ads. A brand can
              have several, and one advertiser can run ads for more than one brand.</div>
          </div>
          <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, maxWidth: 760 }}>
            Counts describe what has been collected, not everything that exists. Each ad image is
            read once into a fixed structure (people, dominant colours, tone, funnel stage, claims),
            and the report counts over that structure rather than asking a model to estimate. An ad
            whose image has not been read is excluded from those breakdowns, which is why the
            percentage above matters.
          </div>
          <div style={{ display: "grid", gap: 3, fontSize: 12, color: "#4b5563", fontWeight: 600 }}>
            <div>Readable creatives: <b>{extractable}</b> (creatives without an image are excluded)</div>
            <div>Read and current: <b>{read_current}</b></div>
            <div>Never attempted: <b>{never_attempted}</b></div>
            <div>Failing: <b>{failing}</b></div>
            <div>Outdated read: <b>{read_stale}</b></div>
            <div>Without a cached screenshot: <b>{no_snapshot}</b></div>
            {extraction_enqueued_last_run != null && (
              <div>Queued by the last fetch run: <b>{extraction_enqueued_last_run}</b></div>
            )}
          </div>

          {failing_sample.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: "#991b1b", marginBottom: 5 }}>
                RECENT FAILURES
              </div>
              <div style={{ display: "grid", gap: 4, maxHeight: 190, overflowY: "auto" }}>
                {failing_sample.map(f => (
                  <div key={f.creative_id} style={{
                    display: "flex", gap: 10, alignItems: "baseline",
                    fontSize: 11.5, color: "#4b5563",
                    borderBottom: "1px solid #f3f4f6", paddingBottom: 3,
                  }}>
                    <span style={{ fontWeight: 800, color: "#111827", whiteSpace: "nowrap" }}>
                      #{f.creative_id}
                    </span>
                    <span style={{ whiteSpace: "nowrap" }}>{f.attempts} attempt{f.attempts === 1 ? "" : "s"}</span>
                    <span style={{
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                    }} title={f.error}>
                      {f.error}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <button
              onClick={load}
              disabled={loading}
              style={{
                border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8,
                padding: "6px 12px", cursor: loading ? "default" : "pointer",
                fontSize: 12, fontWeight: 800, color: "#374151",
              }}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
